// Bridges the host's `tool-changed` DOM CustomEvent into the central
// `WhiteboardBus.tool$` BehaviorSubject. One of four state-stream adapters
// whitelisted by the `single-publisher-invariant` ESLint rule (file suffix
// match).
//
// ToolController dispatches a bubbling CustomEvent on the host element
// (the <o2d-whiteboard>) with `detail: { tool: Tool, emoji?: string }`.
// The adapter listens via `host.addEventListener(...)` and forwards
// `detail.tool` to `bus.tool$`. `dispose()` removes the listener — MUST be
// called from the host's `disconnectedCallback` BEFORE `bus.destroy()`
// (TR9 / FC-6 ordering).

import {
    TOOL_CHANGED_EVENT,
    type ToolChangedDetail,
} from '../../core/tool/tool.types.js';
import type { WhiteboardBus } from './whiteboard-bus.js';

export class ToolBusAdapter {
    readonly #host: HTMLElement;
    readonly #listener: (event: Event) => void;

    constructor(host: HTMLElement, bus: WhiteboardBus) {
        this.#host = host;
        this.#listener = (event: Event): void => {
            const detail = (event as CustomEvent<ToolChangedDetail>).detail;
            bus.tool$.next(detail.tool);
        };
        this.#host.addEventListener(TOOL_CHANGED_EVENT, this.#listener);
    }

    /** Remove the host listener. Idempotent. */
    dispose(): void {
        this.#host.removeEventListener(TOOL_CHANGED_EVENT, this.#listener);
    }
}
